import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Button, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Paper,
  Container, CircularProgress, TextField, FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { collection, getDocs, query, where,} from "firebase/firestore";
import { db } from "../../../firebase";
import Autocomplete from "@mui/material/Autocomplete";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: theme.shadows[3],
  marginBottom: theme.spacing(4),
}));

const ItemWiseDetails = ({ unitCode, companyCode, unitType, companyName, }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const printRef = useRef();

  const queryParams = new URLSearchParams(location.search);

  const urlUnitCode =
    queryParams.get("unit") || location.state?.unitCode || "";

  const effectiveUnitCode = urlUnitCode || unitCode;

  const [salesData, setSalesData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const today = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [serviceArea, setServiceArea] = useState("All");
  const [serviceAreas, setServiceAreas] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("All");
  const [items, setItems] = useState([]);
  const [itemName, setItemName] = useState("All");
  const [city, setCity] = useState("");
const [phone, setPhone] = useState("");
const [gstin, setGstin] = useState("");

useEffect(() => {
  const fetchCompanyDetails = async () => {
    try {
      const companyQuery = query(
  collection(db, "companies"),
  where("code", "==", companyCode)
);
      const snapshot = await getDocs(companyQuery);

      if (!snapshot.empty) {
        const company = snapshot.docs[0].data();

        setPhone(company.phone || "");
      }
    } catch (error) {
      console.error(
        "Error fetching company details:",
        error
      );
    }
  };

  if (companyCode) {
    fetchCompanyDetails();
  }
}, [companyCode]);

useEffect(() => {
  const fetchUnitDetails = async () => {
    try {
      const unitQuery = query(
        collection(db, "units"),
        where("companyCode", "==", companyCode),
        where("unitCode", "==", effectiveUnitCode)
      );

      const snapshot = await getDocs(unitQuery);

      if (!snapshot.empty) {
        const unit = snapshot.docs[0].data();
        setCity(unit.city || "");
        setGstin(unit.gstin || "");
      }
    } catch (error) {
      console.error(
        "Error fetching unit details:",
        error
      );
    }
  };

  if (effectiveUnitCode) {
    fetchUnitDetails();
  }
}, [companyCode, effectiveUnitCode]);

  useEffect(() => {
  const fetchServiceAreas = async () => {
    try {
      const serviceAreaQuery = query(
        collection(db, "serviceAreas"),
        where("companyCode", "==", companyCode),
        where("unitCode", "==", effectiveUnitCode)
      );

      const snapshot = await getDocs(serviceAreaQuery);

      const areas = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setServiceAreas(areas);
    } catch (err) {
      console.error("Error fetching service areas:", err);
    }
  };

  if (effectiveUnitCode) {
    fetchServiceAreas();
  }
}, [effectiveUnitCode, companyCode]);


useEffect(() => {
  const fetchCategories = async () => {
    try {
      const categoryQuery = query(
        collection(db, "categories"),
        where("companyCode", "==", companyCode),
        where("unitCode", "==", effectiveUnitCode)
      );

      const snapshot = await getDocs(categoryQuery);

      const categoryList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setCategories(categoryList);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  if (effectiveUnitCode) {
    fetchCategories();
  }
}, [effectiveUnitCode, companyCode]);


useEffect(() => {
  const fetchItems = async () => {
    try {
      const itemsQuery = query(
        collection(db, "companymenu"),
        where("companyCode", "==", companyCode),
        where("unitCode", "==", effectiveUnitCode)
      );

      const snapshot = await getDocs(itemsQuery);

      const itemList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setItems(itemList);
    } catch (err) {
      console.error("Error fetching items:", err);
    }
  };

  if (effectiveUnitCode) {
    fetchItems();
  }
}, [effectiveUnitCode, companyCode]);

  useEffect(() => {
    const fetchSalesDetails = async () => {
      if (!effectiveUnitCode) {
        setError("Unit code is missing.");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        const ordersQuery = query(
          collection(db, "orders"),
          where("unitCode", "==", effectiveUnitCode)
        );

        const snapshot = await getDocs(ordersQuery);

        const itemMap = {};

snapshot.docs.forEach((doc) => {
  const order = doc.data();

  if (
    !order.billNo ||
    Number(order.billNo) <= 0 ||
    !order.createdAt?.toDate
  ) {
    return;
  }

  const billDate = order.createdAt
    .toDate()
    .toISOString()
    .split("T")[0];

  const matchesDate =
    billDate >= startDate &&
    billDate <= endDate;

  if (!matchesDate) return;

  (order.items || [])
    .filter((item) => {
      const matchesServiceArea =
        serviceArea === "All" ||
        item.serviceArea === serviceArea;

      const matchesCategory =
        category === "All" ||
        item.category === category ||
        item.categoryName === category;

      const matchesItem =
        itemName === "All" ||
        item.itemName === itemName ||
        item.name === itemName;

      const isNotCancelled =
        (item.status || "").toLowerCase() !==
        "cancelled";

      return (
        matchesServiceArea &&
        matchesCategory &&
        matchesItem &&
        isNotCancelled
      );
    })
    .forEach((item) => {
      const itemCode =
        item.itemCode ||
        item.code ||
        "N/A";

      const itemName =
        item.itemName ||
        item.name ||
        "N/A";

      const price = Number(
        item.price || item.rate || 0
      );

      const qty = Number(
        item.quantity || item.qty || 0
      );

      const amount = price * qty;

      const key = `${itemCode}-${itemName}`;

      if (!itemMap[key]) {
        itemMap[key] = {
          itemCode,
          itemName,
          price,
          qty: 0,
          amount: 0,
        };
      }

      itemMap[key].qty += qty;
      itemMap[key].amount += amount;
    });
});

setSalesData(Object.values(itemMap));

        setIsLoading(false);
      } catch (err) {
        console.error(err);
        setError("Failed to load item wise details.");
        setIsLoading(false);
      }
    };

    fetchSalesDetails();
  }, [
    effectiveUnitCode,
    startDate,
    endDate,
    serviceArea,
    category,
    itemName,
  ]);

const handleExportExcel = () => {
  const workbook = XLSX.utils.book_new();

  const excelData = [];

  // TITLE
  excelData.push([
    `Item Wise Details - ${companyName || ""}`,
  ]);

  // EMPTY ROW
  excelData.push([]);

  // TABLE HEADERS
  excelData.push([
  "Sl No",
  "Item Code",
  "Item Name",
  "Price",
  "Qty",
  "Amount",
]);

  const groupedData = {};

  // GROUP DATA
  salesData.forEach((item, index) => {
  excelData.push([
    index + 1,
    item.itemCode,
    item.itemName,
    item.price,
    item.qty,
    item.amount,
  ]);
});

  

  // OVERALL GRAND TOTAL
  excelData.push([
  "",
  "",
  "Grand Total",
  "",
  totalQty,
  totalAmount,
]);

  const worksheet =
    XLSX.utils.aoa_to_sheet(excelData);

  // MERGE CELLS
  worksheet["!merges"] = [];

  // TITLE MERGE
  worksheet["!merges"].push({
    s: { r: 0, c: 0 },
    e: { r: 0, c: 7 },
  });

  // MERGE BILL DATE ROWS
  let rowIndex = 3;

  Object.entries(groupedData).forEach(
    ([billDate, bills]) => {
      worksheet["!merges"].push({
        s: { r: rowIndex, c: 0 },
        e: { r: rowIndex, c: 7 },
      });

      rowIndex++;

      Object.entries(bills).forEach(
        ([billNo, items]) => {
          rowIndex += items.length;
          rowIndex += 1;
        }
      );

      if (
        Object.keys(bills).length > 1
      ) {
        rowIndex += 1;
      }

      rowIndex += 1;
    }
  );

  // COLUMN WIDTHS
  worksheet["!cols"] = [
    { wch: 10 },
    { wch: 22 },
    { wch: 18 },
    { wch: 20 },
    { wch: 35 },
    { wch: 15 },
    { wch: 10 },
    { wch: 18 },
  ];

  // ROW HEIGHTS
worksheet["!rows"] = excelData.map(() => ({
  hpt: 28, // row height
}));

// STYLING + ALIGNMENT
const range =
  XLSX.utils.decode_range(
    worksheet["!ref"]
  );

for (
  let R = range.s.r;
  R <= range.e.r;
  ++R
) {
  for (
    let C = range.s.c;
    C <= range.e.c;
    ++C
  ) {
    const cellAddress =
      XLSX.utils.encode_cell({
        r: R,
        c: C,
      });

    if (!worksheet[cellAddress]) continue;

    // DEFAULT ALIGNMENT
    let horizontalAlign = "left";

    // NUMBER COLUMNS
    if (
      C === 0 || // Sl No
      C === 5 || // Price
      C === 6 || // Qty
      C === 7    // Amount
    ) {
      horizontalAlign = "right";
    }

    // TITLE ROW
    if (R === 0) {
      horizontalAlign = "center";
    }

    // HEADER ROW
    if (R === 2) {
      horizontalAlign = "center";
    }

    // BILL DATE MERGED ROWS
    const rowData = excelData[R];

    if (
      rowData &&
      rowData[0] &&
      String(rowData[0]).startsWith(
        "Bill Date :"
      )
    ) {
      horizontalAlign = "center";
    }

    worksheet[cellAddress].s = {
      alignment: {
        horizontal: horizontalAlign,
        vertical: "center",
      },

      font: {
        bold: R === 0, // ONLY TITLE BOLD
      },
    };
  }
}

  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    "Sales Details"
  );

  // FORMAT DATE
  const currentDate = new Date();

  const formattedDate =
    currentDate
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .replace(/ /g, "_");

  const excelBuffer = XLSX.write(
    workbook,
    {
      bookType: "xlsx",
      type: "array",
      cellStyles: true,
    }
  );

  const data = new Blob(
    [excelBuffer],
    {
      type:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    }
  );

  saveAs(
    data,
    `Item-Wise-Details-${formattedDate}.xlsx`
  );
};

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");

    printWindow.document.write(`
      <html>
        <head>
          <title>Item Wise Details</title>

          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
            }

            h2 {
              text-align: center;
              margin-bottom: 20px;
            }

            table {
              width: 100%;
              border-collapse: collapse;
            }

            table, th, td {
              border: 1px solid #000;
            }

            th, td {
              padding: 8px;
              text-align: center;
            }
          </style>
        </head>

        <body>
          <h2>
  Item Wise Details - ${companyName || ""}
</h2>
          <table>
<tr>
  <th>Sl No</th>
  <th>Item Code</th>
  <th>Item Name</th>
  <th>Price</th>
  <th>Qty</th>
  <th>Amount</th>
</tr>

${salesData
  .map(
    (item, index) => `
      <tr>
        <td>${index + 1}</td>
        <td>${item.itemCode}</td>
        <td>${item.itemName}</td>
        <td>₹ ${item.price.toFixed(2)}</td>
        <td>${item.qty}</td>
        <td>₹ ${item.amount.toFixed(2)}</td>
      </tr>
    `
  )
  .join("")}
  <tr style="font-weight:bold;">
  <td colspan="4" align="right">
    Grand Total
  </td>

  <td>${totalQty}</td>

  <td>
    ₹ ${totalAmount.toFixed(2)}
  </td>
</tr>
</table>

          <script>
            window.onload = () => {
              window.print();
              window.onafterprint = () => window.close();
            };
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  const handleExportPDF = () => {
  const doc = new jsPDF();

let y = 15;

// Company Name
doc.setFontSize(16);
doc.setFont(undefined, "bold");
doc.text(companyName || "", 105, y, { align: "center" });

// Reduced space between Company Name and City
y += 5;

// City + Phone
doc.setFontSize(11); // Increased from 10
doc.setFont(undefined, "normal");
doc.text(
  `${city || ""}, Ph: ${phone || ""}`,
  105,
  y,
  { align: "center" }
);

// Reduced space
y += 5;

// GSTIN
doc.text(
  `GSTIN : ${gstin || ""}`,
  105,
  y,
  { align: "center" }
);

// Reduced space
y += 5;

// Date
doc.text(
  `Date : ${startDate} to ${endDate}`,
  105,
  y,
  { align: "center" }
);

y += 5;

const serviceAreaName =
  serviceArea === "All"
    ? "All"
    : serviceAreas.find(
        (s) => s.id === serviceArea
      )?.serviceAreaName || serviceArea;

// Service Area below Date
doc.text(
  `Service Area : ${serviceAreaName}`,
  105,
  y,
  { align: "center" }
);

y += 5;

// Category below Service Area
doc.text(
  `Category : ${category}`,
  105,
  y,
  { align: "center" }
);

y += 8;

// Report Title
doc.setFont(undefined, "bold");
doc.setFontSize(14);
doc.text(
  "Item Wise Sales Details",
  105,
  y,
  { align: "center" }
);

y += 8;

  autoTable(doc, {
    startY: y,

    head: [[
      "Sl No",
      "Item Code",
      "Item Name",
      "Price",
      "Qty",
      "Amount",
    ]],

    body: salesData.map(
      (item, index) => [
        index + 1,
        item.itemCode,
        item.itemName,
        item.price.toFixed(2),
        item.qty,
        item.amount.toFixed(2),
      ]
    ),

    foot: [[
      "",
      "",
      "Grand Total",
      "",
      totalQty,
      totalAmount.toFixed(2),
    ]],

    showFoot: "lastPage",
    theme: "grid",

    headStyles: {
      fillColor: [103, 80, 164],
    },

    footStyles: {
      fillColor: [220, 220, 220],
      textColor: 0,
      fontStyle: "bold",
    },
  });

  doc.save(
    `Item-Wise-Details-${new Date()
      .toISOString()
      .split("T")[0]}.pdf`
  );
};

  const handleBack = () => {
    navigate("/unit_manager/home", {
      state: {
        unitCode: effectiveUnitCode,
        companyCode,
        companyName,
        unitType,
      },
    });
  };

  useEffect(() => {
  setItemName("All");
}, [category]);

const totalQty = salesData.reduce(
  (sum, item) => sum + Number(item.qty || 0),
  0
);

const totalAmount = salesData.reduce(
  (sum, item) => sum + Number(item.amount || 0),
  0
);

const groupedData = {};

salesData.forEach((item) => {
  if (!groupedData[item.billDate]) {
    groupedData[item.billDate] = {};
  }

  if (!groupedData[item.billDate][item.billNo]) {
    groupedData[item.billDate][item.billNo] = [];
  }

  groupedData[item.billDate][item.billNo].push(item);
});

const hasMultipleBillsOnAnyDate =
  Object.values(groupedData).some(
    (bills) => Object.keys(bills).length > 1
  );

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: "center" }}>
        <CircularProgress />

        <Typography sx={{ mt: 2 }}>
          Loading item wise details...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: "center" }}>
        <Typography color="error">
          {error}
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box ref={printRef}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontWeight: "bold",
            color: "primary.main",
            mb: 4,
            textAlign: "center",
          }}
        >
          Item Wise Details - {companyName}
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 3,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) =>
              setStartDate(e.target.value)
            }
            InputLabelProps={{
              shrink: true,
            }}
          />

          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) =>
              setEndDate(e.target.value)
            }
            InputLabelProps={{
              shrink: true,
            }}
          />

          <FormControl sx={{ minWidth: 220 }}>
  <InputLabel>Service Area</InputLabel>

  <Select
    value={serviceArea}
    label="Service Area"
    onChange={(e) =>
      setServiceArea(e.target.value)
    }
  >
    <MenuItem value="All">
      All
    </MenuItem>

    {serviceAreas.map((area) => (
      <MenuItem
        key={area.id}
        value={area.id}  
      >
        {area.serviceAreaName}  {/* display name */}
      </MenuItem>
    ))}
  </Select>
</FormControl>
<Autocomplete
  sx={{ minWidth: 250 }}
  options={[
    { label: "All", value: "All" },
    ...categories.map((cat) => ({
      label: cat.name,
      value: cat.name,
    })),
  ]}
  getOptionLabel={(option) =>
    typeof option === "string"
      ? option
      : option.label || ""
  }
  isOptionEqualToValue={(option, value) =>
    option.value === value.value
  }
  value={
    [
      { label: "All", value: "All" },
      ...categories.map((cat) => ({
        label: cat.name,
        value: cat.name,
      })),
    ].find((c) => c.value === category) || {
      label: "All",
      value: "All",
    }
  }
  onChange={(event, newValue) => {
    setCategory(
      newValue?.value || "All"
    );
  }}
  renderInput={(params) => (
    <TextField
      {...params}
      label="Category"
    />
  )}
/>

<Autocomplete
  sx={{ minWidth: 250 }}
  options={[
    { label: "All", value: "All" },

    ...items
      .filter((itm) => {
        if (category === "All") return true;

        return (
          itm.category === category ||
          itm.categoryName === category
        );
      })
      .map((itm) => ({
        label: itm.name || itm.itemName,
        value: itm.name || itm.itemName,
      })),
  ]}
  getOptionLabel={(option) =>
    typeof option === "string"
      ? option
      : option.label || ""
  }
  isOptionEqualToValue={(option, value) =>
    option.value === value.value
  }
  value={
    [
      { label: "All", value: "All" },

      ...items
        .filter((itm) => {
          if (category === "All") return true;

          return (
            itm.category === category ||
            itm.categoryName === category
          );
        })
        .map((itm) => ({
          label: itm.name || itm.itemName,
          value: itm.name || itm.itemName,
        })),
    ].find((i) => i.value === itemName) || {
      label: "All",
      value: "All",
    }
  }
  onChange={(event, newValue) => {
    setItemName(
      newValue?.value || "All"
    );
  }}
  renderInput={(params) => (
    <TextField
      {...params}
      label="Item Name"
    />
  )}
/>
        </Box>

        <StyledCard>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
  <TableRow sx={{ bgcolor: "primary.light" }}>
    <TableCell sx={{ fontWeight: "bold", color: "#fff" }}>
      Sl No
    </TableCell>

    <TableCell sx={{ fontWeight: "bold", color: "#fff" }}>
      Item Code
    </TableCell>

    <TableCell sx={{ fontWeight: "bold", color: "#fff" }}>
      Item Name
    </TableCell>

    <TableCell sx={{ fontWeight: "bold", color: "#fff" }}>
      Price
    </TableCell>

    <TableCell sx={{ fontWeight: "bold", color: "#fff" }}>
      Qty
    </TableCell>

    <TableCell sx={{ fontWeight: "bold", color: "#fff" }}>
      Amount
    </TableCell>
  </TableRow>
</TableHead>
<TableBody>
  {salesData.length > 0 ? (
    <>
      {salesData.map((item, index) => (
        <TableRow key={index}>
          <TableCell>
            {index + 1}
          </TableCell>

          <TableCell>
            {item.itemCode}
          </TableCell>

          <TableCell>
            {item.itemName}
          </TableCell>

          <TableCell>
            ₹ {item.price.toFixed(2)}
          </TableCell>

          <TableCell>
            {item.qty}
          </TableCell>

          <TableCell>
            ₹ {item.amount.toFixed(2)}
          </TableCell>
        </TableRow>
      ))}

      <TableRow
        sx={{
          bgcolor: "#cfd8dc",
        }}
      >
        <TableCell
          colSpan={4}
          align="right"
          sx={{
            fontWeight: "bold",
            fontSize: 16,
          }}
        >
          Grand Total
        </TableCell>

        <TableCell
          sx={{
            fontWeight: "bold",
            fontSize: 16,
          }}
        >
          {salesData.reduce(
            (sum, item) =>
              sum + Number(item.qty || 0),
            0
          )}
        </TableCell>

        <TableCell
          sx={{
            fontWeight: "bold",
            fontSize: 16,
          }}
        >
          ₹{" "}
          {salesData
            .reduce(
              (sum, item) =>
                sum +
                Number(item.amount || 0),
              0
            )
            .toFixed(2)}
        </TableCell>
      </TableRow>
    </>
  ) : (
    <TableRow>
      <TableCell
        colSpan={6}
        align="center"
      >
        No item details found
      </TableCell>
    </TableRow>
  )}
</TableBody>
            </Table>
          </TableContainer>
        </StyledCard>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 2,
          mt: 3,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handlePrint}
        >
          Print
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleExportExcel}
        >
          Excel
        </Button>
        <Button
  variant="contained"
  color="error"
  onClick={handleExportPDF}
>
  PDF
</Button>
        <Button
          variant="outlined"
          onClick={handleBack}
        >
          Back
        </Button>
      </Box>
    </Container>
  );
};

export default ItemWiseDetails;